import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Subject } from 'rxjs';

class Border {
  public isBorderMouseDown = false;
  public borderClickX;
  public borderClickY;
  public borderTop;
  public borderLeft;
  public newBorderTop;
  public newBorderLeft;
  public width;
  public height;
}

class Image {
  public imageTop: number;
  public imageLeft: number;
  public isMouseDown = false;
  public topStart: number;
  public leftStart: number;
}

class ResizerPoint {
  public isMouseDown = false;
  public clickX;
  public clickY;
  public event;
}

@Directive({
    selector: '[crop]',
    standalone: false
})
export class CropDirective implements OnInit {
  @Output() getPoints = new EventEmitter();
  @Input() undoTrigger: Subject<any>;
  private parent: any;
  private border;
  private borderModel = new Border();
  private image;
  private imageModel = new Image();
  private topLeft;
  private topRight;
  private bottomLeft;
  private bottomRight;
  private topLeftPoint = new ResizerPoint();
  private topRightPoint = new ResizerPoint();
  private bottomLeftPoint = new ResizerPoint();
  private bottomRightPoint = new ResizerPoint();
  private canDraw = true;

  constructor(private el: ElementRef) {
    this.image = el.nativeElement;
    this.border = this.image.previousElementSibling;
    this.topLeft = this.border.children[0];
    this.topRight = this.border.children[1];
    this.bottomLeft = this.border.children[2];
    this.bottomRight = this.border.children[3];
    this.parent = this.image.parentElement;
    this.parent.addEventListener('mouseup', (event) => {
      this.parentMouseUp();
    });
    this.parent.addEventListener('mouseleave', (event) => {
      this.parentMouseLeave(event);
    });
    this.border.addEventListener('mousemove', (event) => {
      this.borderOnMouseMove(event);
      this.movePoints(event);
    });
    this.border.addEventListener('mouseleave', (event) => {
      this.borderOnMouseLeave(event);
    });
  }

  ngOnInit() {
    this.undoTrigger.subscribe(() => this.undoChanges());
  }

  @HostListener('mousedown', ['$event']) onMouseDown(event) {
    if (this.canDraw) {
      this.imageModel.imageTop = this.image.offsetTop - this.image.offsetHeight / 2;
      this.imageModel.imageLeft = this.image.offsetLeft - this.image.offsetWidth / 2;
      this.imageModel.topStart = event.offsetY;
      this.imageModel.leftStart = event.offsetX;
      this.borderModel.borderTop = this.imageModel.imageTop + event.offsetY;
      this.borderModel.borderLeft = this.imageModel.imageLeft + event.offsetX;
      this.setBorderStyles(this.borderModel.borderTop, this.borderModel.borderLeft);
      this.imageModel.isMouseDown = true;
    }
  }
  @HostListener('mouseup', ['$event']) onMouseUp(event) {
    this.imageModel.isMouseDown = false;
    this.setBorderToMove();
    this.cropImage();
    this.canDraw = false;
  }
  @HostListener('mousemove', ['$event']) onMouseMove(event) {
    if (this.imageModel.isMouseDown) {
      this.border.style.width = `${event.offsetX - this.imageModel.leftStart}px`;
      this.border.style.height = `${event.offsetY - this.imageModel.topStart}px`;
    }
    this.movePoints(event);
  }

  private setBorderStyles(top, left) {
    this.border.style.top = `${top}px`;
    this.border.style.left = `${left}px`;
    this.border.style.zIndex = `100`;
    this.border.style.border = `2px solid black`;
  }

  private parentMouseUp() {
    if (this.imageModel.isMouseDown) {
      this.cropImage();
      this.setBorderToMove();
      this.canDraw = false;
      this.imageModel.isMouseDown = false;
    }
  }

  private borderOnMouseMove(event) {
    if (this.imageModel.isMouseDown) {
      this.border.style.width = `${event.offsetX}px`;
      this.border.style.height = `${event.offsetY}px`;
    }
    if (
      this.borderModel.isBorderMouseDown &&
      !this.topLeftPoint.isMouseDown &&
      !this.topRightPoint.isMouseDown &&
      !this.bottomLeftPoint.isMouseDown &&
      !this.bottomRightPoint.isMouseDown
    ) {
      const x = event.x - this.borderModel.borderClickX;
      const y = event.y - this.borderModel.borderClickY;
      const imageBottom = this.imageModel.imageTop + this.image.offsetHeight;
      const imageRight = this.imageModel.imageLeft + this.image.offsetWidth;
      const borderBottom = this.borderModel.borderTop + this.border.offsetHeight;
      const borderRight = this.borderModel.borderLeft + this.border.offsetWidth;
      if (this.imageModel.imageTop <= this.borderModel.borderTop + y && borderBottom + y <= imageBottom) {
        this.borderModel.newBorderTop = this.borderModel.borderTop + y;
        this.border.style.top = `${this.borderModel.newBorderTop}px`;
      } else {
        this.borderModel.borderTop = this.borderModel.newBorderTop;
        this.borderModel.borderClickY = event.y;
      }
      if (this.imageModel.imageLeft <= this.borderModel.borderLeft + x && borderRight + x <= imageRight) {
        this.borderModel.newBorderLeft = this.borderModel.borderLeft + x;
        this.border.style.left = `${this.borderModel.newBorderLeft}px`;
      } else {
        this.borderModel.borderLeft = this.borderModel.newBorderLeft;
        this.borderModel.borderClickX = event.x;
      }
    }
  }

  private parentMouseLeave(event) {
    if (this.imageModel.isMouseDown) {
      this.imageModel.isMouseDown = false;
      this.setBorderToMove();
      this.canDraw = false;
      this.cropImage();
    }
  }

  private setBorderToMove() {
    this.border.style.cursor = 'move';
    this.border.addEventListener('mousedown', (event) => {
      this.borderMouseDown(event);
    });
    this.border.addEventListener('mouseup', (event) => {
      this.borderMouseUp(event);
    });
    this.setResizeElements();
  }

  private borderMouseDown(event) {
    this.borderModel.isBorderMouseDown = true;
    this.borderModel.borderClickX = event.x;
    this.borderModel.borderClickY = event.y;
  }

  private borderMouseUp(event) {
    if (
      this.borderModel.isBorderMouseDown &&
      !this.topLeftPoint.isMouseDown &&
      !this.topRightPoint.isMouseDown &&
      !this.bottomLeftPoint.isMouseDown &&
      !this.bottomRightPoint.isMouseDown
    ) {
      this.borderModel.isBorderMouseDown = false;
      this.borderModel.borderTop = this.borderModel.newBorderTop;
      this.borderModel.borderLeft = this.borderModel.newBorderLeft;
      this.border.style.top = `${this.borderModel.borderTop}px`;
      this.border.style.left = `${this.borderModel.borderLeft}px`;
    }
    this.cropImage();
  }

  private borderOnMouseLeave(event) {
    if (this.borderModel.isBorderMouseDown) {
      this.borderMouseUp(event);
    }
  }

  private undoChanges() {
    this.cleanBorder();
    this.canDraw = true;
  }

  private cleanBorder() {
    this.border.style.border = '';
    this.border.style.zIndex = '0';
    this.border.style.width = '';
    this.border.style.height = '';
    this.border.style.top = '';
    this.border.style.left = '';
    this.topLeft.style.width = `0px`;
    this.topLeft.style.height = `0px`;
    this.topRight.style.width = `0px`;
    this.topRight.style.height = `0px`;
    this.bottomLeft.style.width = `0px`;
    this.bottomLeft.style.height = `0px`;
    this.bottomRight.style.width = `0px`;
    this.bottomRight.style.height = `0px`;
  }

  private setResizeElements() {
    this.topLeft.style.width = `20px`;
    this.topLeft.style.height = `20px`;
    this.topRight.style.width = `20px`;
    this.topRight.style.height = `20px`;
    this.bottomLeft.style.width = `20px`;
    this.bottomLeft.style.height = `20px`;
    this.bottomRight.style.width = `20px`;
    this.bottomRight.style.height = `20px`;

    this.topLeft.addEventListener('mousedown', (event) => {
      this.topLeftPoint.isMouseDown = true;
      this.topLeftPoint.clickX = event.x;
      this.topLeftPoint.clickY = event.y;
      this.borderModel.width = this.border.offsetWidth;
      this.borderModel.height = this.border.offsetHeight;
    });
    this.topLeft.addEventListener('mousemove', (event) => {
      if (this.topLeftPoint.isMouseDown) {
        this.topLeftMouseMove(event);
      }
    });
    this.topLeft.addEventListener('mouseup', (event) => {
      if (this.topLeftPoint.isMouseDown) {
        this.topLeftMouseUp(event);
      }
    });

    this.topRight.addEventListener('mousedown', (event) => {
      this.topRightPoint.isMouseDown = true;
      this.topRightPoint.clickX = event.x;
      this.topRightPoint.clickY = event.y;
      this.borderModel.width = this.border.offsetWidth;
      this.borderModel.height = this.border.offsetHeight;
    });
    this.topRight.addEventListener('mousemove', (event) => {
      if (this.topRightPoint.isMouseDown) {
        this.topRightMouseMove(event);
      }
    });
    this.topRight.addEventListener('mouseup', (event) => {
      if (this.topRightPoint.isMouseDown) {
        this.topRightMouseUp(event);
      }
    });

    this.bottomLeft.addEventListener('mousedown', (event) => {
      this.bottomLeftPoint.isMouseDown = true;
      this.bottomLeftPoint.clickX = event.x;
      this.bottomLeftPoint.clickY = event.y;
      this.borderModel.width = this.border.offsetWidth;
      this.borderModel.height = this.border.offsetHeight;
    });
    this.bottomLeft.addEventListener('mousemove', (event) => {
      if (this.bottomLeftPoint.isMouseDown) {
        this.bottomLeftMouseMove(event);
      }
    });
    this.bottomLeft.addEventListener('mouseup', (event) => {
      if (this.bottomLeftPoint.isMouseDown) {
        this.bottomLeftMouseUp(event);
      }
    });

    this.bottomRight.addEventListener('mousedown', (event) => {
      this.bottomRightPoint.isMouseDown = true;
      this.bottomRightPoint.clickX = event.x;
      this.bottomRightPoint.clickY = event.y;
      this.borderModel.width = this.border.offsetWidth;
      this.borderModel.height = this.border.offsetHeight;
    });
    this.bottomRight.addEventListener('mousemove', (event) => {
      if (this.bottomRightPoint.isMouseDown) {
        this.bottomRightMouseMove(event);
      }
    });
    this.bottomRight.addEventListener('mouseup', (event) => {
      if (this.bottomRightPoint.isMouseDown) {
        this.bottomRightMouseUp(event);
      }
    });
  }

  private topLeftMouseMove(event) {
    const y = this.topLeftPoint.clickY - event.y;
    const x = this.topLeftPoint.clickX - event.x;
    if (
      this.imageModel.imageTop < this.borderModel.borderTop - y &&
      this.imageModel.imageLeft < this.borderModel.borderLeft - x &&
      this.borderModel.height + y > 20 &&
      this.borderModel.width + x > 20
    ) {
      this.border.style.top = `${this.borderModel.borderTop - y}px`;
      this.border.style.left = `${this.borderModel.borderLeft - x}px`;
      this.border.style.height = `${this.borderModel.height + y}px`;
      this.border.style.width = `${this.borderModel.width + x}px`;
      this.topLeftPoint.event = event;
    } else {
      if (this.topLeftPoint.event) {
        this.topLeftMouseUp(this.topLeftPoint.event);
      }
    }
  }

  private topLeftMouseUp(event) {
    this.topLeftPoint.isMouseDown = false;
    this.borderModel.isBorderMouseDown = false;
    const y = this.topLeftPoint.clickY - event.y;
    const x = this.topLeftPoint.clickX - event.x;
    this.borderModel.height += y;
    this.borderModel.width += x;
    this.borderModel.borderTop -= y;
    this.borderModel.borderLeft -= x;
    this.cropImage();
  }

  private topRightMouseMove(event) {
    const y = this.topRightPoint.clickY - event.y;
    const x = this.topRightPoint.clickX - event.x;
    const imageRight = this.imageModel.imageLeft + this.image.offsetWidth;
    const borderRight = this.borderModel.borderLeft + this.borderModel.width - x;
    if (
      this.imageModel.imageTop < this.borderModel.borderTop - y &&
      imageRight > borderRight &&
      this.borderModel.height + y > 20 &&
      this.borderModel.width - x > 20
    ) {
      this.border.style.top = `${this.borderModel.borderTop - y}px`;
      this.border.style.height = `${this.borderModel.height + y}px`;
      this.border.style.width = `${this.borderModel.width - x}px`;
      this.topRightPoint.event = event;
    } else {
      if (this.topRightPoint.event) {
        this.topRightMouseUp(this.topRightPoint.event);
      }
    }
  }

  private topRightMouseUp(event) {
    this.topRightPoint.isMouseDown = false;
    this.borderModel.isBorderMouseDown = false;
    const y = this.topRightPoint.clickY - event.y;
    const x = this.topRightPoint.clickX - event.x;
    this.borderModel.height += y;
    this.borderModel.width -= x;
    this.borderModel.borderTop -= y;
    this.cropImage();
  }

  private bottomLeftMouseMove(event) {
    const y = this.bottomLeftPoint.clickY - event.y;
    const x = this.bottomLeftPoint.clickX - event.x;
    const imageBottom = this.imageModel.imageTop + this.image.offsetHeight;
    const borderBottom = this.borderModel.borderTop + this.borderModel.height - y;
    if (
      this.imageModel.imageLeft < this.borderModel.borderLeft - x &&
      imageBottom > borderBottom &&
      this.borderModel.height - y > 20 &&
      this.borderModel.width + x > 20
    ) {
      this.border.style.left = `${this.borderModel.borderLeft - x}px`;
      this.border.style.height = `${this.borderModel.height - y}px`;
      this.border.style.width = `${this.borderModel.width + x}px`;
      this.bottomLeftPoint.event = event;
    } else {
      if (this.bottomLeftPoint.event) {
        this.bottomLeftMouseUp(this.bottomLeftPoint.event);
      }
    }
  }

  private bottomLeftMouseUp(event) {
    this.bottomLeftPoint.isMouseDown = false;
    this.borderModel.isBorderMouseDown = false;
    const y = this.bottomLeftPoint.clickY - event.y;
    const x = this.bottomLeftPoint.clickX - event.x;
    this.borderModel.height -= y;
    this.borderModel.width += x;
    this.borderModel.borderLeft -= x;
    this.cropImage();
  }

  private bottomRightMouseMove(event) {
    const y = this.bottomRightPoint.clickY - event.y;
    const x = this.bottomRightPoint.clickX - event.x;
    const imageBottom = this.imageModel.imageTop + this.image.offsetHeight;
    const borderBottom = this.borderModel.borderTop + this.borderModel.height - y;
    const imageRight = this.imageModel.imageLeft + this.image.offsetWidth;
    const borderRight = this.borderModel.borderLeft + this.borderModel.width - x;
    if (
      imageRight > borderRight &&
      imageBottom > borderBottom &&
      this.borderModel.height - y > 20 &&
      this.borderModel.width - x > 20
    ) {
      this.border.style.height = `${this.borderModel.height - y}px`;
      this.border.style.width = `${this.borderModel.width - x}px`;
      this.bottomRightPoint.event = event;
    } else {
      if (this.bottomRightPoint.event) {
        this.bottomRightMouseUp(event);
      }
    }
  }

  private bottomRightMouseUp(event) {
    this.bottomRightPoint.isMouseDown = false;
    this.borderModel.isBorderMouseDown = false;
    const y = this.bottomLeftPoint.clickY - event.y;
    const x = this.bottomLeftPoint.clickX - event.x;
    this.borderModel.height -= y;
    this.borderModel.width -= x;
    this.cropImage();
  }

  private cropImage() {
    const top = this.borderModel.borderTop - this.imageModel.imageTop;
    const left = this.borderModel.borderLeft - this.imageModel.imageLeft;
    const bottom = this.image.offsetHeight - (this.border.offsetHeight + top);
    const right = this.image.offsetWidth - (this.border.offsetWidth + left);
    const coefficient = this.image.naturalWidth / this.image.offsetWidth;

    const points = {
      top: Math.round(top * coefficient),
      left: Math.round(left * coefficient),
      bottom: Math.round(bottom * coefficient),
      right: Math.round(right * coefficient)
    };
    this.getPoints.emit(points);
  }

  private movePoints(event) {
    if (this.topLeftPoint.isMouseDown) {
      this.topLeftMouseMove(event);
    }
    if (this.topRightPoint.isMouseDown) {
      this.topRightMouseMove(event);
    }
    if (this.bottomLeftPoint.isMouseDown) {
      this.bottomLeftMouseMove(event);
    }
    if (this.bottomRightPoint.isMouseDown) {
      this.bottomRightMouseMove(event);
    }
  }
}
